// The vocabulary of the automatic LLM review: the ten assessments it records
// for a finding, with the label and help text each one reads by.
//
// The review's reference columns (policy clause, norm, intent, model
// configuration) are deliberately absent: their values mean nothing to an
// analyst without the profile open.
import { BEHAVIOUR_SEVERITIES, CONTENT_SENSITIVITIES } from "./traceEvaluation.js";
import { effectiveSeverity } from "./reviewFindingFacts.js";

// "off_mission" -> "Off mission".
function humanizeToken(value) {
    const token = String(value ?? "").replace(/[_-]+/g, " ").trim();
    return token ? token.charAt(0).toUpperCase() + token.slice(1) : "";
}

// A field carries either a `ladder`, whose values are ordered worst-last, or
// an unordered `values` set. Callers listing selectable values read whichever
// one is present.
export const REVIEW_REQUIREMENTS = Object.freeze([
    {
        id: "min-severity", field: "effective_severity", ladder: BEHAVIOUR_SEVERITIES,
        label: "Severity",
        help: "The higher of the behaviour severity and the content sensitivity.",
    },
    {
        id: "min-sensitivity", field: "content_sensitivity", ladder: CONTENT_SENSITIVITIES,
        label: "Content sensitivity",
        help: "How sensitive the content in the session was, on its own.",
    },
    {
        id: "reasons", field: "reason", values: ["risk", "sensitivity", "policy", "off_mission"],
        label: "Reasons",
        help: "Why the review flagged the finding.",
    },
    {
        id: "policy-status", field: "policy_status", values: ["violation", "consistent", "no_view"], emptyable: true,
        label: "Policy",
        help: "How the session related to the written policy.",
    },
    {
        id: "norm-status", field: "norm_status", values: ["deviation", "consistent", "no_view"], emptyable: true,
        label: "Norms",
        help: "How the session compared with what is usual for this organization.",
    },
    {
        id: "intent-status", field: "intent_status", values: ["counter", "tangential", "aligned", "no_view"], emptyable: true,
        label: "Intent",
        help: "How the session related to the stated intents.",
    },
    {
        id: "policy-claim", field: "policy_claim_result", values: ["upheld", "refuted", "indeterminate"], emptyable: true,
        label: "Policy claim check",
        help: "Whether the verifier upheld or refuted the review's policy claim.",
    },
    {
        id: "intent-claim", field: "intent_claim_result", values: ["upheld", "refuted", "indeterminate"], emptyable: true,
        label: "Intent claim check",
        help: "Whether the verifier upheld or refuted the review's intent claim.",
    },
    {
        id: "evidence", field: "evidence_status", values: ["complete", "partial", "context_only", "unsupported"],
        label: "Evidence",
        help: "How well the cited evidence supports the finding.",
    },
    {
        id: "verifier", field: "verifier_status", values: ["completed", "skipped", "error"],
        label: "Verifier run",
        help: "Whether the claim verifier ran on the finding.",
    },
]);

export function valueLabel(value) {
    return humanizeToken(value) || String(value ?? "");
}

// The value a finding has for a requirement, so a control can mark where this
// alert sits: a lower-cased string, "" when the review recorded no verdict.
export function findingValue(def, item) {
    if (!item) return null;
    switch (def.field) {
        case "effective_severity": return effectiveSeverity(item);
        case "content_sensitivity": return String(item.contentSensitivity ?? "").toLowerCase();
        case "reason": return String(item.reason ?? "").toLowerCase();
        case "policy_status": return String(item.policyStatus ?? "").toLowerCase();
        case "norm_status": return String(item.normStatus ?? "").toLowerCase();
        case "intent_status": return String(item.intentStatus ?? "").toLowerCase();
        case "policy_claim_result": return String(item.policyClaimResult ?? "").toLowerCase();
        case "intent_claim_result": return String(item.intentClaimResult ?? "").toLowerCase();
        case "evidence_status": return String(item.evidenceStatus ?? "").toLowerCase();
        case "verifier_status": return String(item.verifierStatus ?? "").toLowerCase();
        default: return null;
    }
}
